/**
 * Transaction utilities
 * Utilities for handling transaction types and operations
 */

// ============================================================================
// Types
// ============================================================================

export type FrontendTransactionType =
  | 'ocr'
  | 'manual'
  | 'sales_ocr'
  | 'sales_manual';
export type BackendTransactionType = 'ocr' | 'manual' | 'sales';

// ============================================================================
// Transaction Type Normalization
// ============================================================================

/**
 * Normalizes frontend transaction types to backend-compatible types
 * Maps sales_ocr/sales_manual -> sales for backend API calls
 */
export function normalizeTransactionType(
  frontendType: FrontendTransactionType | null,
): BackendTransactionType | null {
  if (!frontendType) return null;

  switch (frontendType) {
    case 'ocr':
      return 'ocr';
    case 'manual':
      return 'manual';
    case 'sales_ocr':
    case 'sales_manual':
      return 'sales';
    default:
      console.warn(
        `⚠️ [normalizeTransactionType] Unknown transaction type: ${frontendType}`,
      );
      return null;
  }
}

/**
 * Checks if a transaction type supports editing operations
 */
export function canEditTransaction(
  transactionType: FrontendTransactionType | null,
): boolean {
  if (!transactionType) return false;

  // All transaction types can now be edited (removed read-only restriction for sales)
  return (
    transactionType === 'ocr' ||
    transactionType === 'manual' ||
    transactionType === 'sales_ocr' ||
    transactionType === 'sales_manual'
  );
}

/**
 * Checks if a transaction type supports delete operations
 */
export function canDeleteTransaction(
  transactionType: FrontendTransactionType | null,
): boolean {
  if (!transactionType) return false;

  // All transaction types can now be deleted via the unified endpoint
  // OCR, manual, and sales transactions are all supported
  return ['ocr', 'manual', 'sales_ocr', 'sales_manual'].includes(
    transactionType,
  );
}

/**
 * Gets user-friendly label for transaction type
 */
export function getTransactionTypeLabel(
  transactionType: FrontendTransactionType | null,
): string {
  if (!transactionType) return 'Transacción';

  switch (transactionType) {
    case 'ocr':
      return 'Servicios (Escaneado)';
    case 'manual':
      return 'Servicios (Manual)';
    case 'sales_ocr':
      return 'Ventas (Escaneado)';
    case 'sales_manual':
      return 'Ventas (Manual)';
    default:
      return 'Transacción';
  }
}

/**
 * Checks if transaction type is a sales transaction
 */
export function isSalesTransaction(
  transactionType: FrontendTransactionType | null,
): boolean {
  return transactionType === 'sales_ocr' || transactionType === 'sales_manual';
}
